/**
 * EF-CREW-PWA-PHASE-1-SHIFT-INTERACTION-V1
 * 
 * Crew Shift List Component
 * Displays assigned shifts with Accept/Reject actions for pending shifts.
 * 
 * Features:
 * - Displays shift cards with date, time, role, location, status
 * - Accept/Reject buttons for pending shifts only
 * - Instant UI update on action (no page reload)
 * - Duplicate click prevention
 * - Loading states and error handling
 */
import { useState, useEffect, useCallback } from "react";
import { useI18n } from "../../i18n/I18nProvider";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { toast } from "sonner";
import {
  Calendar,
  Clock,
  MapPin,
  Briefcase,
  Check,
  X,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Ban,
  LogOut,
} from "lucide-react";
// EVENTSFLO-CREW-PORTAL-WITHDRAW-AND-MESSAGES-V1
import { withdrawMyAssignment } from "../../lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Format date for display
 */
function formatDate(dateStr) {
  if (!dateStr) return "TBD";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

/**
 * Format time range for display
 */
function formatTimeRange(startTime, endTime) {
  if (!startTime) return "TBD";
  const formatTime = (t) => {
    if (!t) return "";
    // Handle HH:MM format
    if (typeof t === "string" && t.includes(":")) {
      return t.substring(0, 5);
    }
    return t;
  };
  const start = formatTime(startTime);
  const end = formatTime(endTime);
  return end ? `${start} - ${end}` : start;
}

/**
 * Status badge component
 */
function StatusBadge({ status }) {
  const { t } = useI18n();
  const configs = {
    pending: {
      variant: "outline",
      className: "border-amber-500/50 text-amber-500 bg-amber-500/10",
      icon: Clock,
      label: t("crewPortal.shiftList.statusPending"),
    },
    accepted: {
      variant: "outline",
      className: "border-emerald-500/50 text-emerald-500 bg-emerald-500/10",
      icon: CheckCircle2,
      label: t("crewPortal.shiftList.statusAccepted"),
    },
    rejected: {
      variant: "outline",
      className: "border-red-500/50 text-red-500 bg-red-500/10",
      icon: XCircle,
      label: t("crewPortal.shiftList.statusRejected"),
    },
    // EVENTSFLO-CREW-PORTAL-WITHDRAW-AND-MESSAGES-V1
    crew_cancelled: {
      variant: "outline",
      className: "border-orange-500/50 text-orange-500 bg-orange-500/10",
      icon: LogOut,
      label: t("crewPortal.shiftList.statusWithdrawn"),
    },
    cancelled: {
      variant: "outline",
      className: "border-red-600/60 text-red-600 bg-red-600/10",
      icon: Ban,
      label: t("crewPortal.shiftList.statusCancelled"),
    },
  };

  const config = configs[status] || configs.pending;
  const Icon = config.icon;

  return (
    <Badge variant={config.variant} className={config.className} data-testid={`shift-status-badge-${status}`}>
      <Icon className="h-3 w-3 mr-1" />
      {config.label}
    </Badge>
  );
}

/**
 * Single Shift Card component
 */
function ShiftCard({ shift, onAccept, onReject, onWithdraw, actionInProgress }) {
  const { t } = useI18n();
  const isActionInProgress = actionInProgress === shift.shift_id;
  const isAnyActionInProgress = actionInProgress !== null;

  return (
    <Card 
      className={`transition-all duration-200 ${
        shift.is_past ? "opacity-60" : ""
      }`}
      data-testid={`shift-card-${shift.shift_id}`}
    >
      <CardContent className="p-4">
        {/* Header: Role and Status */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-primary" />
            <span className="font-semibold text-foreground" data-testid="shift-role">
              {shift.role}
            </span>
          </div>
          <StatusBadge status={shift.status} />
        </div>

        {/* Details Grid */}
        <div className="space-y-2 text-sm">
          {/* Date */}
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span data-testid="shift-date">{formatDate(shift.date)}</span>
            {shift.is_past && (
              <Badge variant="secondary" className="text-xs ml-1">{t("crewPortal.shiftList.pastBadge")}</Badge>
            )}
          </div>

          {/* Time */}
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span data-testid="shift-time">
              {formatTimeRange(shift.start_time, shift.end_time)}
            </span>
          </div>

          {/* Location */}
          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin className="h-4 w-4" />
            <div className="flex flex-col">
              <span data-testid="shift-location">{shift.location}</span>
              {shift.location_address && (
                <span className="text-xs opacity-70">{shift.location_address}</span>
              )}
            </div>
          </div>

          {/* EF-SHIFT-MEETING-PLACE-PWA-V1: Meeting Place - prominently displayed */}
          {shift.meeting_place && (
            <div 
              className="mt-3 p-3 bg-primary/5 border border-primary/20 rounded-lg"
              data-testid="shift-meeting-place"
            >
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-1">
                    {t("crewPortal.shiftList.meetingPlace")}
                  </p>
                  <p className="text-sm text-foreground whitespace-pre-wrap break-words">
                    {shift.meeting_place}
                  </p>
                  {/* Open in Maps button - if meeting place looks like an address */}
                  {shift.meeting_place.length > 10 && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(shift.meeting_place)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-2 text-xs text-primary hover:underline"
                      data-testid="open-in-maps-btn"
                    >
                      <MapPin className="h-3 w-3" />
                      {t("crewPortal.shiftList.openInMaps")}
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Project name if available */}
          {shift.project_name && (
            <div className="text-xs text-muted-foreground mt-2 pt-2 border-t">
              {t("crewPortal.shiftList.projectPrefix", { name: shift.project_name })}
            </div>
          )}
        </div>

        {/* Action Buttons - Only for pending, non-past shifts */}
        {shift.can_respond && shift.status === "pending" && (
          <div className="flex gap-2 mt-4 pt-3 border-t">
            <Button
              size="sm"
              variant="default"
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              onClick={() => onAccept(shift.shift_id)}
              disabled={isAnyActionInProgress}
              data-testid={`accept-btn-${shift.shift_id}`}
            >
              {isActionInProgress ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Check className="h-4 w-4 mr-1" />
              )}
              {t("crewPortal.shiftList.acceptShift")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1 border-red-500/50 text-red-500 hover:bg-red-500/10"
              onClick={() => onReject(shift.shift_id)}
              disabled={isAnyActionInProgress}
              data-testid={`reject-btn-${shift.shift_id}`}
            >
              {isActionInProgress ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <X className="h-4 w-4 mr-1" />
              )}
              {t("crewPortal.shiftList.rejectAction")}
            </Button>
          </div>
        )}

        {/* EVENTSFLO-CREW-PORTAL-WITHDRAW-AND-MESSAGES-V1: Withdraw button for accepted shifts */}
        {shift.status === "accepted" && !shift.is_past && (
          <div className="mt-4 pt-3 border-t">
            <Button
              size="sm"
              variant="outline"
              className="w-full border-orange-500/60 text-orange-600 hover:bg-orange-500/10"
              onClick={() => onWithdraw(shift)}
              disabled={isAnyActionInProgress}
              data-testid={`withdraw-btn-${shift.shift_id}`}
            >
              {isActionInProgress ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <LogOut className="h-4 w-4 mr-1" />
              )}
              {t("crewPortal.shiftList.cancelWithdrawAction")}
            </Button>
          </div>
        )}

        {/* EVENTSFLO-CREW-PORTAL-WITHDRAW-AND-MESSAGES-V1: Admin-cancelled notice */}
        {shift.status === "cancelled" && (
          <div
            className="mt-4 pt-3 border-t flex items-start gap-2 text-sm text-red-600"
            data-testid={`shift-cancelled-notice-${shift.shift_id}`}
          >
            <Ban className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">{t("crewPortal.shiftList.cancelledByAdminNotice")}</p>
              {shift.cancelled_at && (
                <p className="text-xs opacity-80">
                  {t("crewPortal.shiftList.cancelledAt", { when: new Date(shift.cancelled_at).toLocaleString("en-GB") })}
                </p>
              )}
            </div>
          </div>
        )}

        {/* EVENTSFLO-CREW-PORTAL-WITHDRAW-AND-MESSAGES-V1: Withdrawn-by-crew summary */}
        {shift.status === "crew_cancelled" && (
          <div
            className="mt-4 pt-3 border-t flex items-start gap-2 text-sm text-orange-600"
            data-testid={`shift-withdrawn-notice-${shift.shift_id}`}
          >
            <LogOut className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">{t("crewPortal.shiftList.youWithdrewNotice")}</p>
              {shift.cancel_reason && (
                <p className="text-xs opacity-80">{t("crewPortal.shiftList.reasonPrefix", { reason: shift.cancel_reason })}</p>
              )}
              {shift.cancelled_at && (
                <p className="text-xs opacity-80">
                  {new Date(shift.cancelled_at).toLocaleString("en-GB")}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Show response timestamp for non-pending */}
        {shift.status !== "pending" && shift.status !== "cancelled" && shift.status !== "crew_cancelled" && shift.responded_at && (
          <div className="text-xs text-muted-foreground mt-3 pt-2 border-t">
            {t("crewPortal.shiftList.respondedAt", { when: new Date(shift.responded_at).toLocaleString("en-GB") })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Empty state component
 */
function EmptyState() {
  const { t } = useI18n();
  return (
    <div className="text-center py-12" data-testid="shifts-empty-state">
      <Briefcase className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
      <p className="text-muted-foreground">{t("crewPortal.shiftList.empty")}</p>
      <p className="text-sm text-muted-foreground mt-2">
        {t("crewPortal.shiftList.emptyHint")}
      </p>
    </div>
  );
}

/**
 * Loading state component
 */
function LoadingState() {
  return (
    <div className="flex items-center justify-center py-12" data-testid="shifts-loading">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

/**
 * Error state component
 */
function ErrorState({ error, onRetry }) {
  const { t } = useI18n();
  return (
    <div className="text-center py-12" data-testid="shifts-error-state">
      <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
      <p className="text-destructive font-medium">{t("crewPortal.shiftList.loadFailed")}</p>
      <p className="text-sm text-muted-foreground mt-2">{error}</p>
      <Button variant="outline" size="sm" onClick={onRetry} className="mt-4">
        <RefreshCw className="h-4 w-4 mr-2" />
        {t("crewPortal.tryAgain")}
      </Button>
    </div>
  );
}

/**
 * Summary stats component
 */
function ShiftSummary({ stats }) {
  const { t } = useI18n();
  return (
    <div 
      className="flex gap-4 mb-4 text-sm"
      data-testid="shifts-summary"
    >
      <div className="flex items-center gap-1">
        <span className="text-muted-foreground">{t("crewPortal.shiftList.summary.totalLabel")}</span>
        <span className="font-medium">{stats.total}</span>
      </div>
      {stats.pending_count > 0 && (
        <div className="flex items-center gap-1 text-amber-500">
          <Clock className="h-3 w-3" />
          <span>{t("crewPortal.shiftList.summary.pendingLabel", { count: stats.pending_count })}</span>
        </div>
      )}
      {stats.accepted_count > 0 && (
        <div className="flex items-center gap-1 text-emerald-500">
          <CheckCircle2 className="h-3 w-3" />
          <span>{t("crewPortal.shiftList.summary.acceptedLabel", { count: stats.accepted_count })}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Main CrewShiftList component
 */
export function CrewShiftList() {
  const { t } = useI18n();
  const [shifts, setShifts] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending_count: 0, accepted_count: 0, rejected_count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(null); // Tracks shift_id being processed
  // EVENTSFLO-CREW-PORTAL-WITHDRAW-AND-MESSAGES-V1
  const [withdrawTarget, setWithdrawTarget] = useState(null); // { shift_id, assignment_id, role, date }
  const [withdrawReason, setWithdrawReason] = useState("");
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);

  /**
   * Fetch shifts from API
   */
  const fetchShifts = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem("eventsflo_auth_token");
      if (!token) {
        throw new Error("Authentication required");
      }

      const response = await fetch(`${API_URL}/api/crew/shifts`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Failed to fetch shifts (${response.status})`);
      }

      const data = await response.json();
      setShifts(data.shifts || []);
      setStats({
        total: data.total || 0,
        pending_count: data.pending_count || 0,
        accepted_count: data.accepted_count || 0,
        rejected_count: data.rejected_count || 0,
      });
    } catch (err) {
      console.error("Error fetching shifts:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  /**
   * Handle Accept action
   */
  const handleAccept = async (shiftId) => {
    // Prevent duplicate clicks
    if (actionInProgress) {
      return;
    }

    setActionInProgress(shiftId);

    try {
      const token = localStorage.getItem("eventsflo_auth_token");
      const response = await fetch(`${API_URL}/api/crew/shifts/${shiftId}/accept`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || "Failed to accept shift");
      }

      const result = await response.json();

      // Update local state immediately (optimistic update)
      setShifts((prev) =>
        prev.map((s) =>
          s.shift_id === shiftId
            ? { ...s, status: "accepted", can_respond: false, responded_at: result.responded_at }
            : s
        )
      );

      // Update stats
      setStats((prev) => ({
        ...prev,
        pending_count: Math.max(0, prev.pending_count - 1),
        accepted_count: prev.accepted_count + 1,
      }));

      toast.success(t("shiftList.acceptSuccess"));
    } catch (err) {
      console.error("Accept error:", err);
      toast.error(err.message || t("shiftList.acceptFailed"));
    } finally {
      setActionInProgress(null);
    }
  };

  /**
   * Handle Reject action
   */
  const handleReject = async (shiftId) => {
    // Prevent duplicate clicks
    if (actionInProgress) {
      return;
    }

    setActionInProgress(shiftId);

    try {
      const token = localStorage.getItem("eventsflo_auth_token");
      const response = await fetch(`${API_URL}/api/crew/shifts/${shiftId}/reject`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || "Failed to reject shift");
      }

      const result = await response.json();

      // Update local state immediately (optimistic update)
      setShifts((prev) =>
        prev.map((s) =>
          s.shift_id === shiftId
            ? { ...s, status: "rejected", can_respond: false, responded_at: result.responded_at }
            : s
        )
      );

      // Update stats
      setStats((prev) => ({
        ...prev,
        pending_count: Math.max(0, prev.pending_count - 1),
        rejected_count: prev.rejected_count + 1,
      }));

      toast.success(t("crewPortal.shiftList.declineSuccess"));
    } catch (err) {
      console.error("Reject error:", err);
      toast.error(err.message || t("crewPortal.shiftList.rejectFailed"));
    } finally {
      setActionInProgress(null);
    }
  };

  /**
   * EVENTSFLO-CREW-PORTAL-WITHDRAW-AND-MESSAGES-V1
   * Opens the withdraw confirmation dialog
   */
  const handleOpenWithdraw = useCallback((shift) => {
    setWithdrawReason("");
    setWithdrawTarget({
      shift_id: shift.shift_id,
      assignment_id: shift.assignment_id || shift.id,
      role: shift.role,
      date: shift.date,
    });
  }, []);

  /**
   * EVENTSFLO-CREW-PORTAL-WITHDRAW-AND-MESSAGES-V1
   * Submits the withdraw request via JWT endpoint
   */
  const handleConfirmWithdraw = async () => {
    if (!withdrawTarget || !withdrawTarget.assignment_id) {
      toast.error(t("crewPortal.shiftList.withdrawMissingAssignment"));
      setWithdrawTarget(null);
      return;
    }
    setWithdrawSubmitting(true);
    setActionInProgress(withdrawTarget.shift_id);
    try {
      const reason = withdrawReason.trim();
      const res = await withdrawMyAssignment(
        withdrawTarget.assignment_id,
        reason || undefined,
      );
      const data = res?.data || {};
      setShifts((prev) =>
        prev.map((s) =>
          s.shift_id === withdrawTarget.shift_id
            ? {
                ...s,
                status: "crew_cancelled",
                can_respond: false,
                can_withdraw: false,
                cancel_reason: reason || s.cancel_reason,
                cancelled_at: data.cancelled_at || new Date().toISOString(),
              }
            : s,
        ),
      );
      setStats((prev) => ({
        ...prev,
        accepted_count: Math.max(0, prev.accepted_count - 1),
      }));
      if (data.already_withdrawn) {
        toast.success(t("crewPortal.shiftList.alreadyWithdrawn"));
      } else {
        toast.success(t("crewPortal.shiftList.withdrawSuccess"));
      }
      setWithdrawTarget(null);
    } catch (err) {
      console.error("Withdraw error:", err);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 410) {
        toast.error(detail || t("crewPortal.shiftList.withdrawCancelledByAdmin"));
        // Sync local card to cancelled state
        setShifts((prev) =>
          prev.map((s) =>
            s.shift_id === withdrawTarget.shift_id ? { ...s, status: "cancelled" } : s,
          ),
        );
        setWithdrawTarget(null);
      } else if (status === 400) {
        toast.error(detail || t("crewPortal.shiftList.withdrawOnlyAccepted"));
      } else {
        toast.error(detail || t("crewPortal.shiftList.withdrawFailed"));
      }
    } finally {
      setWithdrawSubmitting(false);
      setActionInProgress(null);
    }
  };

  // Render states
  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchShifts} />;
  }

  if (shifts.length === 0) {
    return <EmptyState />;
  }

  return (
    <div data-testid="crew-shift-list">
      {/* Summary stats */}
      <ShiftSummary stats={stats} />

      {/* Shift cards */}
      <div className="space-y-4">
        {shifts.map((shift) => (
          <ShiftCard
            key={shift.shift_id}
            shift={shift}
            onAccept={handleAccept}
            onReject={handleReject}
            onWithdraw={handleOpenWithdraw}
            actionInProgress={actionInProgress}
          />
        ))}
      </div>

      {/* EVENTSFLO-CREW-PORTAL-WITHDRAW-AND-MESSAGES-V1: Withdraw confirmation dialog */}
      <AlertDialog
        open={!!withdrawTarget}
        onOpenChange={(open) => {
          if (!open && !withdrawSubmitting) setWithdrawTarget(null);
        }}
      >
        <AlertDialogContent data-testid="withdraw-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("crewPortal.shiftList.withdrawDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {withdrawTarget
                ? t("crewPortal.shiftList.withdrawDialog.descriptionWithRole", {
                    role: withdrawTarget.role || t("crewPortal.shiftList.withdrawDialog.descriptionGeneric"),
                    date: withdrawTarget.date
                      ? t("crewPortal.shiftList.withdrawDialog.descriptionDateSuffix", { date: formatDate(withdrawTarget.date) })
                      : "",
                  })
                : t("crewPortal.shiftList.withdrawDialog.descriptionGeneric")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="withdraw-reason" className="text-sm">
              {t("crewPortal.shiftList.withdrawDialog.reasonLabel")}
            </Label>
            <Textarea
              id="withdraw-reason"
              value={withdrawReason}
              onChange={(e) => setWithdrawReason(e.target.value)}
              placeholder={t("crewPortal.shiftList.withdrawDialog.reasonPlaceholder")}
              rows={3}
              disabled={withdrawSubmitting}
              data-testid="withdraw-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={withdrawSubmitting} data-testid="withdraw-cancel-btn">
              {t("crewPortal.shiftList.withdrawDialog.keep")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmWithdraw();
              }}
              disabled={withdrawSubmitting}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="withdraw-confirm-btn"
            >
              {withdrawSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  {t("crewPortal.shiftList.withdrawDialog.withdrawing")}
                </>
              ) : (
                t("crewPortal.shiftList.withdrawDialog.confirm")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default CrewShiftList;
